import { Link } from "react-router-dom"
import { useEffect, useRef, useState, useCallback } from "react"
import Navbar from "@/components/Navbar"
import Footer from "@/components/Footer"
import {Shield, Users, Eye, Car, Building2, ShoppingBag, Briefcase, Construction, Utensils, Factory, ArrowRight, ArrowUpRight, CheckCircle2, Clock, Award, Headphones, Quote, ChevronLeft, ChevronRight, CircleParking} from 'lucide-react'

const LOGO_URL = "https://static.lumi.new/4c/4c8f124f3e19139e5d5f616a0dcd86e1.png"

const HERO_SLIDES = [
  {
    image: "https://static.lumi.new/75/759249e183822a1258dfd52a6f8992f9.png",
    headline: "Protecting What",
    highlight: "Matters Most",
    sub: "Comprehensive security solutions for residential, commercial, and corporate properties across Canada and the USA.",
  },
  {
    image: "https://static.lumi.new/6f/6f3163a2a910b655d378e37e4be41501.png",
    headline: "24/7 Mobile Patrol",
    highlight: "& Rapid Response",
    sub: "Professional mobile patrol services with detailed reporting, real-time tracking, and immediate incident response.",
  },
  {
    image: "https://static.lumi.new/bd/bde3e95387947f8fd6a30c6e72681c69.jpg",
    headline: "Advanced Surveillance",
    highlight: "& Monitoring",
    sub: "State-of-the-art CCTV monitoring and security operations center staffed by trained professionals around the clock.",
  },
  {
    image: "https://static.lumi.new/22/227372e02c74271a7a3ecc6e2f688a4c.png",
    headline: "Residential",
    highlight: "& Concierge Security",
    sub: "Premium front desk and lobby security services for condominiums, strata buildings, and luxury residential properties.",
  },
]

function useInView() {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("animate-fade-up")
            entry.target.classList.remove("opacity-0")
            observer.unobserve(entry.target)
          }
        })
      },
      { threshold: 0.1 }
    )
    const el = ref.current
    if (el) {
      const children = el.querySelectorAll("[data-animate]")
      children.forEach((child) => observer.observe(child))
    }
    return () => observer.disconnect()
  }, [])
  return ref
}

function Home() {
  const sectionRef = useInView()
  const [currentSlide, setCurrentSlide] = useState(0)
  const [isTransitioning, setIsTransitioning] = useState(false)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const goToSlide = useCallback((index: number) => {
    if (isTransitioning) return
    setIsTransitioning(true)
    setCurrentSlide(index)
    setTimeout(() => setIsTransitioning(false), 800)
  }, [isTransitioning])

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % HERO_SLIDES.length)
  }, [currentSlide, goToSlide])

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length)
  }, [currentSlide, goToSlide])

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setIsTransitioning((prev) => {
        if (!prev) {
          setCurrentSlide((c) => (c + 1) % HERO_SLIDES.length)
          setTimeout(() => setIsTransitioning(false), 800)
          return true
        }
        return prev
      })
    }, 6000)
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [])

  const services = [
    { icon: <Users className="w-6 h-6" />, title: "Uniformed Security", desc: "Professional on-site guards providing 24/7 monitoring and access control." },
    { icon: <Car className="w-6 h-6" />, title: "Mobile Patrol", desc: "Regular patrol services with detailed reporting and rapid response." },
    { icon: <Building2 className="w-6 h-6" />, title: "Concierge Security", desc: "Premium front desk services for residential and commercial properties." },
    { icon: <Construction className="w-6 h-6" />, title: "Site Protection", desc: "Comprehensive construction site security and equipment protection." },
    { icon: <ShoppingBag className="w-6 h-6" />, title: "Loss Prevention", desc: "Expert retail security to protect inventory and ensure customer safety." },
    { icon: <Eye className="w-6 h-6" />, title: "CCTV Monitoring", desc: "Professional surveillance with real-time monitoring and incident response." },
    { icon: <Briefcase className="w-6 h-6" />, title: "Corporate Security", desc: "Complete security solutions for office buildings and corporate environments." },
    { icon: <Shield className="w-6 h-6" />, title: "Executive Protection", desc: "Discreet personal protection for high-profile individuals and executives." },
    { icon: <CircleParking className="w-6 h-6" />, title: "Parking Management", desc: "Complete parking facility management with access control and enforcement." },
  ]

  const industries = [
    { icon: <Building2 className="w-6 h-6" />, name: "Residential & Strata" },
    { icon: <Briefcase className="w-6 h-6" />, name: "Commercial" },
    { icon: <Construction className="w-6 h-6" />, name: "Construction" },
    { icon: <ShoppingBag className="w-6 h-6" />, name: "Retail" },
    { icon: <Utensils className="w-6 h-6" />, name: "Hospitality" },
    { icon: <Users className="w-6 h-6" />, name: "Events" },
    { icon: <Factory className="w-6 h-6" />, name: "Industrial" },
  ]

  const testimonials = [
    {
      name: "Sarah Mitchell",
      role: "Property Manager",
      text: "Sentrio has been protecting our properties for over three years. Their professionalism and reliability are unmatched in the industry.",
    },
    {
      name: "James Chen",
      role: "Construction Director",
      text: "We trust Sentrio with all our construction sites. Their patrol services have prevented countless incidents and saved us thousands.",
    },
    {
      name: "Maria Rodriguez",
      role: "Retail Operations Manager",
      text: "The security team is professional, courteous, and always vigilant. Our customers feel safe and our shrinkage has dropped significantly.",
    },
  ]

  const slide = HERO_SLIDES[currentSlide]

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050A14" }} ref={sectionRef}>
      <Navbar />

      {/* ===== HERO CAROUSEL ===== */}
      <section className="relative min-h-screen flex items-center overflow-hidden">
        {/* Background Images */}
        {HERO_SLIDES.map((s, i) => (
          <div
            key={i}
            className="absolute inset-0 transition-opacity duration-1000 ease-in-out"
            style={{ opacity: currentSlide === i ? 1 : 0 }}
          >
            <img
              src={s.image}
              alt={s.headline}
              className="w-full h-full object-cover scale-105"
              style={{
                transition: "transform 8s ease-out",
                transform: currentSlide === i ? "scale(1.08)" : "scale(1)",
              }}
            />
          </div>
        ))}

        {/* Overlay Gradients */}
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, #050A14, rgba(5,10,20,0.65), rgba(5,10,20,0.45))" }} />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to right, rgba(5,10,20,0.93), rgba(5,10,20,0.55), transparent)" }} />

        {/* Content */}
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-20 sm:pb-28 w-full">
          <div className="max-w-3xl">
            {/* Logo Badge */}
            <div className="mb-8 opacity-0 animate-fade-up flex items-center gap-4">
              <img src={LOGO_URL} alt="Sentrio Security" className="h-20 sm:h-24 w-20 sm:w-24 rounded-full object-cover" />
              <span className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-wide">Sentrio Security</span>
            </div>

            {/* Headline - transitions with slide */}
            <div key={currentSlide} className="animate-fade-up">
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[1.05] mb-6">
                {slide.headline}
                <br />
                <span className="text-slate-400">{slide.highlight}</span>
              </h1>

              <p className="text-lg sm:text-xl text-slate-400 max-w-xl leading-relaxed mb-10">
                {slide.sub}
              </p>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 mb-16 opacity-0 animate-fade-up stagger-3">
              <Link
                to="/contact"
                className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-gray-900 font-semibold tracking-wide rounded hover:bg-slate-200 transition-all"
              >
                GET FREE CONSULTATION
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/services"
                className="inline-flex items-center justify-center gap-2 px-8 py-4 text-white font-semibold tracking-wide rounded border border-slate-600 hover:bg-white/5 hover:border-slate-400 transition-all"
              >
                VIEW SERVICES
              </Link>
            </div>

            {/* Trust Strip */}
            <div className="flex flex-wrap gap-x-8 gap-y-4 pt-8 border-t border-slate-700/40 opacity-0 animate-fade-up stagger-4">
              {[
                { icon: <CheckCircle2 className="w-4 h-4" />, text: "Licensed & Insured" },
                { icon: <Clock className="w-4 h-4" />, text: "24/7 Operations" },
                { icon: <Award className="w-4 h-4" />, text: "Highly Trained Guards" },
                { icon: <Headphones className="w-4 h-4" />, text: "Dedicated Support" },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-2 text-slate-500 text-sm">
                  <span className="text-slate-400">{item.icon}</span>
                  {item.text}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Carousel Controls */}
        <div className="absolute bottom-8 sm:bottom-12 right-5 sm:right-8 z-20 flex items-center gap-4">
          {/* Slide Indicators */}
          <div className="flex items-center gap-2 mr-2">
            {HERO_SLIDES.map((_, i) => (
              <button
                key={i}
                onClick={() => goToSlide(i)}
                className="relative h-1 rounded-full overflow-hidden transition-all duration-500"
                style={{
                  width: currentSlide === i ? "32px" : "12px",
                  backgroundColor: currentSlide === i ? "rgba(255,255,255,0.9)" : "rgba(255,255,255,0.25)",
                }}
                aria-label={`Go to slide ${i + 1}`}
              >
                {currentSlide === i ? (
                  <span
                    className="absolute inset-0 bg-white/40 origin-left"
                    style={{
                      animation: "progress 6s linear forwards",
                    }}
                  />
                ) : null}
              </button>
            ))}
          </div>

          {/* Prev / Next */}
          <button
            onClick={prevSlide}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-slate-600 text-slate-400 hover:bg-white hover:text-gray-900 hover:border-white transition-all"
            aria-label="Previous slide"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <button
            onClick={nextSlide}
            className="w-10 h-10 flex items-center justify-center rounded-full border border-slate-600 text-slate-400 hover:bg-white hover:text-gray-900 hover:border-white transition-all"
            aria-label="Next slide"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      </section>

      {/* ===== STATS ===== */}
      <section className="relative" style={{ backgroundColor: "#0A1224", borderTop: "1px solid #111D35", borderBottom: "1px solid #111D35" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4">
            {[
              { value: "7+", label: "Years Experience" },
              { value: "500+", label: "Clients Protected" },
              { value: "24/7", label: "Operations Center" },
              { value: "99%", label: "Client Satisfaction" },
            ].map((stat, i) => (
              <div
                key={i}
                className="py-10 sm:py-14 text-center"
                style={i < 3 ? { borderRight: "1px solid #111D35" } : undefined}
              >
                <div className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-xs sm:text-sm text-slate-500 tracking-wider uppercase">
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== MOBILE PATROL FEATURE ===== */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#050A14" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="relative rounded-lg overflow-hidden" data-animate>
            <img
              src="https://static.lumi.new/0c/0cbc878ac3afda5a94138d3773724259.png"
              alt="Sentrio Security mobile patrol vehicle"
              className="w-full h-[300px] sm:h-[400px] lg:h-[500px] object-cover"
            />
            <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(5,10,20,0.85), rgba(5,10,20,0.2))" }} />
            <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10">
              <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-3 block">Mobile Patrol Fleet</span>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-3">Sentrio Security <span className="text-slate-400">On The Move</span></h3>
              <p className="text-slate-400 max-w-xl text-sm sm:text-base">Our branded patrol vehicles provide visible deterrence and rapid response across your properties, 24 hours a day, 7 days a week.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== PARKING MANAGEMENT FEATURE ===== */}
      <section className="pb-24 sm:pb-32" style={{ backgroundColor: "#050A14" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-6 rounded-lg overflow-hidden" style={{ border: "1px solid #111D35" }} data-animate>
            {/* Image */}
            <div className="relative h-[280px] sm:h-[360px] lg:h-auto">
              <img
                src="https://static.lumi.new/7f/7fe61170966e2295ded12cf6c809aaa2.png"
                alt="Sentrio Security parking management services"
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 lg:hidden" style={{ background: "linear-gradient(to top, rgba(10,18,36,1), transparent)" }} />
            </div>
            {/* Content */}
            <div className="p-8 sm:p-10 lg:p-12 flex flex-col justify-center" style={{ backgroundColor: "#0A1224" }}>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 flex items-center justify-center rounded-lg text-slate-300" style={{ backgroundColor: "rgba(30,45,69,0.5)", border: "1px solid #1A2A4A" }}>
                  <CircleParking className="w-6 h-6" />
                </div>
                <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase">Parking Management</span>
              </div>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-4">Complete Parking <span className="text-slate-400">Solutions</span></h3>
              <p className="text-slate-400 leading-relaxed mb-8">From access control and enforcement to traffic flow optimization and revenue management — Sentrio Security delivers end-to-end parking facility management for commercial, residential, and mixed-use properties.</p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  "Access & Gate Control",
                  "Enforcement & Compliance",
                  "Traffic Flow Management",
                  "Revenue Administration",
                ].map((feature, i) => (
                  <div key={i} className="flex items-center gap-2 text-sm text-slate-400">
                    <CheckCircle2 className="w-4 h-4 text-slate-500 flex-shrink-0" />
                    {feature}
                  </div>
                ))}
              </div>
              <Link
                to="/services"
                className="group inline-flex items-center gap-2 text-slate-300 font-semibold hover:text-white transition-colors w-fit"
              >
                Learn More
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#050A14" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          {/* Header */}
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-16" data-animate>
            <div>
              <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Our Services
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight">
                Security Solutions
                <br />
                <span className="text-slate-600">Tailored to You</span>
              </h2>
            </div>
            <Link
              to="/services"
              className="group flex items-center gap-2 text-slate-300 font-semibold hover:text-white transition-colors"
            >
              View All Services
              <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
            </Link>
          </div>

          {/* Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px rounded-lg overflow-hidden" style={{ backgroundColor: "#111D35", border: "1px solid #111D35" }}>
            {services.map((service, i) => (
              <Link
                to="/services"
                key={i}
                className="group p-8 hover:brightness-125 transition-all duration-300 relative"
                style={{ backgroundColor: "#050A14" }}
              >
                <div className="absolute top-0 left-0 w-0 group-hover:w-full h-px bg-gradient-to-r from-slate-400 to-slate-500 transition-all duration-500" />
                <div className="text-slate-600 group-hover:text-slate-300 mb-6 transition-colors">
                  {service.icon}
                </div>
                <h3 className="text-lg font-bold text-white mb-2 group-hover:text-slate-200 transition-colors">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-500 leading-relaxed">{service.desc}</p>
                <ArrowUpRight className="w-4 h-4 text-slate-700 group-hover:text-slate-400 mt-6 transition-colors" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== WHY CHOOSE US ===== */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#0A1224" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
            {/* Image */}
            <div className="relative" data-animate>
              <div className="aspect-[3/4] overflow-hidden rounded-lg">
                <img
                  src="https://static.lumi.new/5d/5d31433afde670710ac267780e6772ea.png"
                  alt="Sentrio Security team"
                  className="w-full h-full object-cover"
                />
              </div>
              {/* Floating card */}
              <div className="absolute -bottom-6 -right-4 sm:-right-6 p-6 rounded-lg shadow-2xl" style={{ backgroundColor: "#050A14", border: "1px solid #1A2A4A" }}>
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-lg" style={{ backgroundColor: "rgba(30,45,69,0.5)", border: "1px solid #1A2A4A" }}>
                    <Shield className="w-7 h-7 text-slate-300" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-white">500+</div>
                    <div className="text-xs text-slate-500 tracking-wider uppercase">
                      Clients Protected
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content */}
            <div data-animate>
              <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Why Sentrio
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
                Your Safety Is
                <br />
                <span className="text-slate-600">Our Priority</span>
              </h2>
              <p className="text-slate-400 text-lg leading-relaxed mb-10">
                With over 7 years of experience protecting businesses and properties
                across North America, we deliver reliable, professional security
                services you can trust.
              </p>

              <div className="space-y-6">
                {[
                  { icon: <Shield className="w-5 h-5" />, title: "Licensed & Certified", desc: "All guards fully licensed and trained to the highest standards" },
                  { icon: <Clock className="w-5 h-5" />, title: "24/7 Monitoring", desc: "Round-the-clock protection and rapid response" },
                  { icon: <CheckCircle2 className="w-5 h-5" />, title: "Custom Solutions", desc: "Security plans tailored to your specific needs" },
                  { icon: <Headphones className="w-5 h-5" />, title: "Professional Support", desc: "Dedicated account managers and support team" },
                ].map((item, i) => (
                  <div key={i} className="flex gap-4 group">
                    <div className="flex-shrink-0 w-12 h-12 flex items-center justify-center rounded-lg text-slate-300 group-hover:bg-white group-hover:text-gray-900 transition-all" style={{ backgroundColor: "rgba(30,45,69,0.5)", border: "1px solid #1A2A4A" }}>
                      {item.icon}
                    </div>
                    <div>
                      <h3 className="font-bold text-white mb-0.5">{item.title}</h3>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>

              <Link
                to="/about"
                className="group inline-flex items-center gap-2 mt-10 text-slate-300 font-semibold hover:text-white transition-colors"
              >
                Learn More About Us
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ===== INDUSTRIES ===== */}
      <section className="py-24" style={{ backgroundColor: "#050A14", borderTop: "1px solid #111D35" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16" data-animate>
            <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Industries
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              Sectors We <span className="text-slate-600">Protect</span>
            </h2>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-4">
            {industries.map((industry, i) => (
              <Link
                to="/services"
                key={i}
                className="group flex flex-col items-center justify-center p-6 sm:p-8 rounded-lg hover:brightness-125 transition-all text-center"
                style={{ backgroundColor: "#0A1224", border: "1px solid #111D35" }}
              >
                <div className="text-slate-600 group-hover:text-slate-300 mb-3 transition-colors">
                  {industry.icon}
                </div>
                <span className="text-xs font-semibold text-slate-500 group-hover:text-white tracking-wide transition-colors">
                  {industry.name}
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#0A1224" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="text-center mb-16" data-animate>
            <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
              Our Process
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
              How It <span className="text-slate-600">Works</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
            {[
              { step: "01", title: "Consultation", desc: "We assess your property, identify risks, and understand your unique security requirements." },
              { step: "02", title: "Custom Plan", desc: "Our team designs a tailored security strategy with the right combination of services and technology." },
              { step: "03", title: "Deploy & Monitor", desc: "We deploy trained professionals and provide ongoing monitoring, reporting, and optimization." },
            ].map((item, i) => (
              <div key={i} className="relative group" data-animate>
                <div className="text-7xl sm:text-8xl font-bold transition-colors leading-none mb-4" style={{ color: "#111D35" }}>
                  {item.step}
                </div>
                <div className="w-12 h-px bg-slate-500 mb-6" />
                <h3 className="text-xl font-bold text-white mb-3">{item.title}</h3>
                <p className="text-slate-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Link
              to="/contact"
              className="group inline-flex items-center gap-3 px-8 py-4 bg-white text-gray-900 font-semibold tracking-wide rounded hover:bg-slate-200 transition-all"
            >
              START YOUR CONSULTATION
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-24 sm:py-32" style={{ backgroundColor: "#050A14", borderTop: "1px solid #111D35" }}>
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex flex-col lg:flex-row justify-between items-start lg:items-end gap-6 mb-16" data-animate>
            <div>
              <span className="text-slate-400 text-xs font-bold tracking-[0.2em] uppercase mb-4 block">
                Testimonials
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white">
                Trusted Across <span className="text-slate-600">North America</span>
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((t, i) => (
              <div
                key={i}
                className="p-8 rounded-lg hover:brightness-110 transition-all group relative"
                style={{ backgroundColor: "#0A1224", border: "1px solid #111D35" }}
              >
                <Quote className="w-8 h-8 mb-6" style={{ color: "rgba(26,42,74,0.6)" }} />
                <div className="flex gap-1 mb-6">
                  {[...Array(5)].map((_, j) => (
                    <svg key={j} className="w-4 h-4 text-slate-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                    </svg>
                  ))}
                </div>
                <p className="text-slate-400 leading-relaxed mb-8">{t.text}</p>
                <div className="pt-6" style={{ borderTop: "1px solid #111D35" }}>
                  <div className="font-bold text-white">{t.name}</div>
                  <div className="text-xs text-slate-600 mt-0.5">{t.role}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="relative py-24 sm:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://static.lumi.new/51/51d8c17d6c85ad15a35aebf3d810f0b6.png"
            alt="Security consultation meeting"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ backgroundColor: "rgba(5,10,20,0.88)" }} />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-5 sm:px-8 text-center">
          <img src={LOGO_URL} alt="Sentrio Security" className="h-16 sm:h-20 w-16 sm:w-20 rounded-full object-cover mx-auto mb-8" />
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
            Ready to Protect
            <br />
            <span className="text-slate-400">Your Property?</span>
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto mb-10">
            Get a free security consultation and discover how Sentrio can keep your
            business, property, and people safe.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="group inline-flex items-center justify-center gap-3 px-10 py-5 bg-white text-gray-900 font-semibold text-lg tracking-wide rounded hover:bg-slate-200 transition-all"
            >
              GET FREE CONSULTATION
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </Link>
            <a
              href="mailto:info@sentriosecurity.com"
              className="inline-flex items-center justify-center gap-2 px-10 py-5 text-white font-semibold text-lg tracking-wide rounded border border-slate-600 hover:bg-white/5 hover:border-slate-400 transition-all"
            >
              EMAIL US
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default Home
